import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTracks } from "@/context/TrackContext";
import { toast } from "sonner";
import { ArrowUpRight, MessageSquare, Library as LibraryIcon, Award, Download } from "lucide-react";
import { Leaderboard } from "@/components/Leaderboard";

export default function Dashboard() {
  const { user } = useAuth();
  const { activeTrack } = useTracks();
  const [stages, setStages] = useState([]);
  const [progress, setProgress] = useState({ percent: 0, completed: 0, total_lessons: 0, by_stage: {} });
  const [cert, setCert] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get("/stages").then((r) => setStages(r.data));
    api.get("/progress").then((r) => setProgress(r.data));
    api.get("/certificate").then((r) => setCert(r.data)).catch(() => {});
  }, []);

  const downloadCert = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/certificate/download", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `certificate-${cert?.certificate?.certificate_id || "completion"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download certificate");
    } finally { setDownloading(false); }
  };

  return (
    <div className="space-y-16">
      {/* Hero: north-star metric */}
      <section className="grid lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-8">
          <div className="flex items-center gap-4 mb-4 flex-wrap">
            <div className="label-eyebrow">Onboarding progress</div>
            {activeTrack && (
              <div
                data-testid="active-track-badge"
                className="inline-flex items-center gap-2 border px-3 py-1 text-xs font-mono tracking-wide"
                style={{ borderColor: activeTrack.accent || "var(--brand-primary)", color: activeTrack.accent || "var(--brand-primary)" }}
              >
                {activeTrack.name}
              </div>
            )}
            {progress.journey_day && (
              <div
                data-testid="journey-day-badge"
                className="inline-flex items-center gap-2 border px-3 py-1 text-xs font-mono tracking-wide"
                style={{ borderColor: "var(--brand-primary)", color: "var(--brand-primary)" }}
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--brand-primary)" }} />
                Day {progress.journey_day} of {progress.journey_length || 14}
                {progress.journey_label && <span className="opacity-70">· {progress.journey_label}</span>}
              </div>
            )}
          </div>
          <div className="flex items-baseline gap-6 flex-wrap">
            <div className="font-display text-7xl md:text-8xl leading-none tracking-tight" data-testid="progress-percent">
              {progress.percent}<span className="text-3xl text-neutral-400">%</span>
            </div>
            <div className="text-sm text-neutral-500 max-w-xs">
              {progress.completed} of {progress.total_lessons} lessons complete. Keep the compounding going, {user?.name?.split(" ")[0]}.
            </div>
          </div>
          <div className="progress-hairline mt-8 w-full max-w-2xl" data-testid="progress-bar">
            <div className="fill" style={{ transform: `scaleX(${Math.min(1, (progress.percent||0)/100)})` }} />
          </div>
        </div>

        <div className="lg:col-span-4 grid grid-cols-2 gap-4">
          <Link to="/library" className="card-hairline p-6 group" data-testid="quick-library">
            <LibraryIcon size={18} strokeWidth={1.25} className="text-[var(--brand-primary)]" />
            <div className="mt-8 font-display text-xl leading-tight">Resource Library</div>
            <div className="mt-1 text-sm text-neutral-500">Scripts, templates, PDFs.</div>
            <ArrowUpRight className="mt-4 text-neutral-400 group-hover:text-black transition-colors" size={18} />
          </Link>
          <Link to="/scenarios" className="card-hairline p-6 group" data-testid="quick-scenarios">
            <MessageSquare size={18} strokeWidth={1.25} className="text-[var(--brand-primary)]" />
            <div className="mt-8 font-display text-xl leading-tight">Role-Play Studio</div>
            <div className="mt-1 text-sm text-neutral-500">Live objections. Live coaching.</div>
            <ArrowUpRight className="mt-4 text-neutral-400 group-hover:text-black transition-colors" size={18} />
          </Link>
        </div>
      </section>

      {/* Certificate of completion */}
      {cert?.earned && (
        <section
          className="border-2 border-[color:var(--brand-primary)] bg-white p-8 md:p-10 flex flex-wrap items-center justify-between gap-6"
          data-testid="certificate-card"
        >
          <div className="flex items-start gap-5">
            <div className="w-14 h-14 flex items-center justify-center shrink-0" style={{ border: "1.5px solid var(--brand-primary)" }}>
              <Award size={26} strokeWidth={1.25} className="text-[var(--brand-primary)]" />
            </div>
            <div>
              <div className="label-eyebrow text-[var(--brand-primary)] mb-2">Curriculum complete</div>
              <div className="font-display text-2xl md:text-3xl leading-tight">Your Certificate of Completion is ready.</div>
              <div className="mt-2 text-sm text-neutral-500">
                Certificate ID <span className="font-mono text-[var(--brand-primary)]" data-testid="certificate-id">{cert.certificate?.certificate_id}</span>
                {cert.certificate?.emailed && " · A copy was emailed to you"}
              </div>
              <button
                onClick={() => {
                  navigator.clipboard?.writeText(`${window.location.origin}/verify/${cert.certificate?.certificate_id}`);
                  toast.success("Verification link copied — share it anywhere");
                }}
                className="mt-3 text-xs link-underline text-[var(--brand-primary)]"
                data-testid="certificate-verify-link-btn"
              >
                Copy public verification link
              </button>
            </div>
          </div>
          <button
            onClick={downloadCert}
            disabled={downloading}
            className="inline-flex items-center gap-2 bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-hover)] text-white px-7 py-3.5 font-semibold rounded-sm transition-colors disabled:opacity-60"
            data-testid="certificate-download-btn"
          >
            <Download size={16} /> {downloading ? "Preparing…" : "Download certificate (PDF)"}
          </button>
        </section>
      )}

      {/* Six stages bento */}
      <section>
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="label-eyebrow mb-3">The Six Stages</div>
            <h2 className="font-display text-3xl md:text-4xl">Your curriculum</h2>
          </div>
          <div className="text-xs text-neutral-500 hidden md:block">Complete stages in any order. Certified at 100%.</div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 lg:grid-cols-12 gap-6">
          {stages.map((s, idx) => {
            const per = progress.by_stage?.[s.key];
            const pct = per?.total ? Math.round((per.done / per.total) * 100) : 0;
            const wide = idx === 0;
            const isLab = s.key === "performance";
            return (
              <Link
                key={s.key}
                to={isLab ? "/call-review" : `/stage/${s.key}`}
                data-testid={`stage-card-${s.key}`}
                className={`card-hairline p-8 ${wide ? "md:col-span-6 lg:col-span-7" : "md:col-span-3 lg:col-span-5"}
                            ${idx===1?"lg:col-span-5":""} ${idx===2?"lg:col-span-6":""} ${idx===3?"lg:col-span-6":""} ${idx===4?"lg:col-span-6":""} ${idx===5?"lg:col-span-6":""}`}
              >
                <div className="flex items-start justify-between">
                  <div className="font-mono text-xs text-neutral-400">0{s.order}</div>
                  <div className="label-eyebrow" style={{color: s.accent}}>{isLab ? "Call Review Studio" : `${s.module_count} modules`}</div>
                </div>
                <div className="mt-10 font-display text-3xl md:text-4xl leading-[1.05]">{s.title}</div>
                <div className="mt-3 text-neutral-500 max-w-lg">{s.subtitle}</div>
                {isLab ? (
                  <div className="mt-8 flex items-center gap-2 text-sm font-semibold" style={{ color: s.accent }}>
                    Upload a call for AI coaching <ArrowUpRight size={15} />
                  </div>
                ) : (
                <div className="mt-8 flex items-center justify-between gap-6">
                  <div className="progress-hairline flex-1">
                    <div className="fill" style={{ transform: `scaleX(${pct/100})`, background: s.accent }} />
                  </div>
                  <div className="text-sm font-mono text-neutral-500 w-12 text-right">{pct}%</div>
                </div>
                )}
              </Link>
            );
          })}
        </div>
      </section>

      <Leaderboard />
    </div>
  );
}
